import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router-dom";
import { Button, Icon, Modal } from "semantic-ui-react";

import { AppointmentStatusCell, DATE_FORMATS_FNS, ReactTable, useSelectedLocation } from "components";
import { useKiosksLabelsQuery } from "modules/KioskDashboard/components/KioskLabelsModal/hooks";
import "modules/KioskDashboard/components/KioskLabelsModal/KioskLabelsModal.scss";
import { PATHS } from "router/paths";
import { formatDate } from "utils/dateHelperFunctions";

import type { Kiosk, KioskLabel } from "models";

const columnHelper = createColumnHelper<KioskLabel>();

type KioskLabelsModalProps = {
  kiosk: Kiosk | null;
  handleClose: () => void;
};

const handleOpenAppointmentDetailPage = (id: number) => window.open(generatePath(PATHS.APPOINTMENT_DETAIL, { id }), "_blank");

export const KioskLabelsModal = ({ kiosk, handleClose }: KioskLabelsModalProps) => {
  const { t } = useTranslation();

  const { selectedLocation } = useSelectedLocation();
  const { data: kioskLabels, isFetching: isKioskLabelsFetching } = useKiosksLabelsQuery(kiosk?.id);

  const columns = [
    columnHelper.accessor(row => ({ row }), {
      id: "index",
      header: () => <span className="index-column-header"> # </span>,
      size: 65,
      cell: ({
        row: {
          original: { number }
        }
      }) => (
        <div className="kiosk-label-tag-number">
          <span className="kiosk-label-tag-number-text">{number}</span>
        </div>
      )
    }),
    columnHelper.accessor(row => row, {
      id: "tag_key",
      header: () => <span>{t("v8_key", "Key")}</span>,
      size: 105,
      cell: ({
        row: {
          original: { appointment }
        }
      }) => (
        <div className="key-container">
          <Icon name={appointment ? "key" : "close"} className={appointment ? "" : "key-container-close"} />
        </div>
      )
    }),
    columnHelper.accessor(row => row, {
      id: "status",
      header: () => <span>{t("v8_status", "Status")}</span>,
      size: 120,
      cell: ({
        row: {
          original: { appointment }
        }
      }) => {
        if (!appointment) return null;

        return (
          <AppointmentStatusCell
            shadow={false}
            value={{
              id: appointment.appointment_status_identifier,
              customcom_status: appointment.customer_communication?.status,
              car_check_paused_at: appointment.car_check_paused_at,
              car_out_of_shop: appointment.car_out_of_shop,
              car_in_shop: appointment.car_in_shop
            }}
            statuses={selectedLocation?.statuses}
          />
        );
      }
    }),
    columnHelper.accessor(row => row, {
      id: "wo_nr",
      header: () => <span>{t("v8_wo", "WO")}</span>,
      size: 250,
      cell: ({
        row: {
          original: { appointment }
        }
      }) => {
        if (!appointment) return null;

        return (
          <Button className="external-link-btn" size="small" onClick={() => handleOpenAppointmentDetailPage(appointment.id)}>
            <Icon className="external link alt" color="black" />
            <span>{appointment.wo_nr}</span>
          </Button>
        );
      }
    }),
    columnHelper.accessor(row => row, {
      id: "appointment_date",
      header: () => <span>{t("v8_appointment_date", "Appointment Date")}</span>,
      size: 750,
      cell: ({
        row: {
          original: { appointment }
        }
      }) => {
        if (!appointment?.time_car_app) return null;

        return <span>{formatDate(appointment.time_car_app, DATE_FORMATS_FNS.dateMonthYear)}</span>;
      }
    })
  ];

  return (
    <Modal open={Boolean(kiosk)} className="KioskLabelsModal">
      <Modal.Header>
        <div className="kiosk-labels-modal-header">
          <h3 className="kiosk-labels-modal-header-title">{kiosk?.name || ""}</h3>
          <Button className="kiosk-labels-modal-appointment-status" size="small" onClick={handleClose}>
            <Icon className="xmark grey" />
          </Button>
        </div>
      </Modal.Header>
      <Modal.Content scrolling>
        <ReactTable isFetching={isKioskLabelsFetching} columns={columns} data={kioskLabels} />
      </Modal.Content>
    </Modal>
  );
};
